import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchBillingDetailsFilterOptions,
  approveCommission,
  payCommission,
  createCommission,
  deleteCommission,
  fetchBirthReport,
  fetchCollectionDetails,
  fetchCollectionReport,
  fetchCommissionReport,
  fetchDeathReport,
  fetchDailyReport,
  fetchDischargeReport,
  fetchDoctorPayoutReport,
  fetchHospitalStayReport,
  fetchInvestigationReport,
  fetchMyCollectionReport,
  fetchPatientReport,
  fetchReportBillingDetails,
  fetchReportBills,
  fetchReportReceipts,
  fetchReportRefunds,
  fetchRevenueReport,
  updateCommission,
} from './reports.api';
import type {
  BirthReportFilters,
  CommissionReportFilters,
  CommissionLineInput,
  ReportSection,
  CollectionDetailFilters,
  DischargeReportFilters,
  InvestigationReportFilters,
  HospitalStayFilters,
  PatientReportFilters,
  PatientReportModule,
  ReportListFilters,
} from './types';

export const useCollectionReport = (filters: { fromDate?: string; toDate?: string }) =>
  useQuery({ queryKey: ['reports', 'collection', filters], queryFn: () => fetchCollectionReport(filters) });

export const useDailyReport = (date: string) =>
  useQuery({ queryKey: ['reports', 'daily', date], queryFn: () => fetchDailyReport(date) });

export const useCollectionDetails = (filters: CollectionDetailFilters) =>
  useQuery({ queryKey: ['reports', 'collection-details', filters], queryFn: () => fetchCollectionDetails(filters) });

export const useMyCollectionReport = (filters: { fromDate?: string; toDate?: string }) =>
  useQuery({ queryKey: ['reports', 'my-collection', filters], queryFn: () => fetchMyCollectionReport(filters) });

export const useRevenueReport = (filters: { fromDate?: string; toDate?: string }) =>
  useQuery({ queryKey: ['reports', 'revenue', filters], queryFn: () => fetchRevenueReport(filters) });

export const useHospitalStayReport = (filters: HospitalStayFilters) =>
  useQuery({ queryKey: ['reports', 'hospital-stay', filters], queryFn: () => fetchHospitalStayReport(filters), placeholderData: keepPreviousData });

export const useReportBills = (filters: ReportListFilters) =>
  useQuery({ queryKey: ['reports', 'bills', filters], queryFn: () => fetchReportBills(filters) });

export const useReportReceipts = (filters: ReportListFilters) =>
  useQuery({ queryKey: ['reports', 'receipts', filters], queryFn: () => fetchReportReceipts(filters) });

export const useReportRefunds = (filters: ReportListFilters) =>
  useQuery({ queryKey: ['reports', 'refunds', filters], queryFn: () => fetchReportRefunds(filters) });

// Master lists change rarely; keep the suggestions for the whole session once loaded.
export const useBillingDetailsFilterOptions = () =>
  useQuery({ queryKey: ['reports', 'billing-details', 'filter-options'], queryFn: fetchBillingDetailsFilterOptions, staleTime: Infinity });

export const useReportBillingDetails = (filters: ReportListFilters) =>
  useQuery({ queryKey: ['reports', 'billing-details', filters], queryFn: () => fetchReportBillingDetails(filters) });

export const usePatientReport = (module: PatientReportModule, filters: PatientReportFilters) =>
  useQuery({ queryKey: ['reports', 'patients', module, filters], queryFn: () => fetchPatientReport(module, filters) });

export const useInvestigationReport = (filters: InvestigationReportFilters) =>
  useQuery({ queryKey: ['reports', 'investigation', filters], queryFn: () => fetchInvestigationReport(filters) });

export const useDoctorPayoutReport = (filters: ReportListFilters) =>
  useQuery({ queryKey: ['reports', 'doctor-payout', filters], queryFn: () => fetchDoctorPayoutReport(filters) });

export const useCommissionReport = (filters: CommissionReportFilters) =>
  useQuery({ queryKey: ['reports', 'commission', filters], queryFn: () => fetchCommissionReport(filters), placeholderData: keepPreviousData });

const useCommissionMutation = <TVariables,>(mutationFn: (variables: TVariables) => Promise<unknown>) => {
  const client = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => void client.invalidateQueries({ queryKey: ['reports', 'commission'] }) });
};

export const useCreateCommission = () => useCommissionMutation((payload: { module: ReportSection; sourceId: string; lines: CommissionLineInput[] }) => createCommission(payload));
export const useUpdateCommission = () => useCommissionMutation(({ id, lines }: { id: string; lines: CommissionLineInput[] }) => updateCommission(id, lines));
export const useDeleteCommission = () => useCommissionMutation((id: string) => deleteCommission(id));
export const useApproveCommission = () => useCommissionMutation((id: string) => approveCommission(id));
export const usePayCommission = () => useCommissionMutation((id: string) => payCommission(id));

export const useBirthReport = (filters: BirthReportFilters) =>
  useQuery({ queryKey: ['reports', 'birth', filters], queryFn: () => fetchBirthReport(filters) });

export const useDeathReport = (filters: ReportListFilters) =>
  useQuery({ queryKey: ['reports', 'death', filters], queryFn: () => fetchDeathReport(filters), placeholderData: keepPreviousData });

export const useDischargeReport = (filters: DischargeReportFilters) =>
  useQuery({ queryKey: ['reports', 'discharge', filters], queryFn: () => fetchDischargeReport(filters) });

